import { MessagingPlatform } from '@this+that/models';

export const HOST_NAME = 'https://assistant.thisandthat.chat';

// Social media links
export const INSTAGRAM_LINK = 'https://www.instagram.com/thisandthatfeed';
export const LINKEDIN_LINK = 'https://www.linkedin.com/company/thisandthat';
export const X_LINK = 'https://x.com/thisandthatfeed';

// Website link
export const WEBSITE_LINK = 'https://thisandthat.chat';

// Unsubscribe link
export const UNSUBSCRIBE_LINK = `${HOST_NAME}/account`;

// Support link
export const SUPPORT_LINK = `${HOST_NAME}/support`;

// Time constants
export const MILLISECONDS_PER_SECOND = 1000;
export const SECONDS_PER_MINUTE = 60;
export const MINUTES_PER_HOUR = 60;
export const HOURS_PER_DAY = 24;
export const FIFTEEN_MINUTES_IN_MILLISECONDS =
  15 * SECONDS_PER_MINUTE * MILLISECONDS_PER_SECOND;
export const DUE_SOON_THRESHOLD_MS =
  HOURS_PER_DAY *
  MINUTES_PER_HOUR *
  SECONDS_PER_MINUTE *
  MILLISECONDS_PER_SECOND;

export const BANNER_TYPE_NEW_FEATURE = 'newFeature';
export const BANNER_TYPE_TIP = 'tip';
export const BANNER_TYPE_CUSTOM = 'custom';
export const TIP_DISPLAY_DAYS = 4;

export const TNT_AI = 't+t AI';

export const RESET_PASSWORD = 'resetPassword'; // url param

export const FILE_NAME_DELIMITER = '-T_T_DELIMITER-';
export const SYSTEM_ACTOR = 't+t';

/**
 * Upload ceiling for assistant composer attachments - enforced client-side and
 * in the presigned POST policy. Deliberately below the 25MB
 * MAX_UPLOAD_SIZE_BYTES in apps/api/src/utils/constants/s3.ts, which the Brain
 * and DoBox attachment policies use: a composer image is forwarded to paths
 * that will not take 25MB anyway.
 */
export const ASSISTANT_MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

const EMAIL_PLATFORM_TUPLE = [
  MessagingPlatform.GMAIL,
  MessagingPlatform.OUTLOOK,
] as const;

export const EMAIL_PLATFORMS: MessagingPlatform[] = [...EMAIL_PLATFORM_TUPLE];

export type EmailPlatformEnumValue =
  Uppercase<`${(typeof EMAIL_PLATFORM_TUPLE)[number]}`>;

// Here rather than in either agent-tool file so neither imports the other — agentTools is
// the SQS worker's, and that import drags its whole graph into the API bundle.
export const EMAIL_PLATFORM_ENUM = EMAIL_PLATFORM_TUPLE.map(
  (platform) => platform.toUpperCase() as EmailPlatformEnumValue,
);

export const EMAIL_PLATFORM_OUTLOOK: EmailPlatformEnumValue = 'OUTLOOK';

// Every platform a stored communication carries. The card keys (google, microsoft,
// google-calendar, outlook-calendar) never do, so a filter on one matches nothing.
export const COMMUNICATION_PLATFORMS = [
  MessagingPlatform.GMAIL,
  MessagingPlatform.OUTLOOK,
  MessagingPlatform.GOOGLE_CHAT,
  MessagingPlatform.SLACK,
  MessagingPlatform.WHATSAPP,
  MessagingPlatform.INSTAGRAM,
  MessagingPlatform.MESSENGER,
  MessagingPlatform.TEAMS,
  MessagingPlatform.GITHUB,
  MessagingPlatform.GOOGLE_BUSINESS,
] as const;

export type CommunicationPlatform = (typeof COMMUNICATION_PLATFORMS)[number];

export const REAL_TIME_COMMUNICATION_PLATFORMS = [
  MessagingPlatform.SLACK,
  MessagingPlatform.TEAMS,
  MessagingPlatform.GOOGLE_CHAT,
  MessagingPlatform.INSTAGRAM,
];

export const MENTION_PICKER_SOURCES = [
  MessagingPlatform.GMAIL,
  MessagingPlatform.OUTLOOK,
  MessagingPlatform.SLACK,
  MessagingPlatform.TEAMS,
  MessagingPlatform.THIS_THAT,
];

// Gmail custom label prefix (labels like "Label_123", "Label_Work", etc.)
export const CUSTOM_LABEL_PREFIX = 'Label_';

// HTTP status codes
export const HTTP_STATUS_UNAUTHORIZED = 401;
export const HTTP_STATUS_FORBIDDEN = 403;
export const HTTP_STATUS_RATE_LIMITED = 429;
export const HTTP_STATUS_SERVER_ERROR_MIN = 500;

// DoBox URLs
export const DOBOX_BASE_URL = `${HOST_NAME}/do-box`;

export const ASSISTANT_CONVERSATION_PATH = '/assistant/';

// MCP OAuth callback path
export const MCP_OAUTH_CALLBACK_PATH = '/integrations/mcp-oauth-callback';

export const MCP_OAUTH_CLIENT_CREDENTIALS_REQUIRED_ERROR =
  'OAUTH_CLIENT_CREDENTIALS_REQUIRED';

// RFC 2822 email address formatting
export const RFC2822_SPECIAL_CHARS = /[,;()"<>@\\[\]]/;

// Minimum minutes a scheduler start date must be in the future at write time.
// The buffer absorbs the lag between the GraphQL write and the stream-driven CreateSchedule/UpdateSchedule call.
export const SCHEDULER_MIN_START_BUFFER_MINUTES = 10;

// Scheduled-flow schedules are named `${UCA_SCHEDULE_NAME_PREFIX}.<userId>.<agentId>`.
export const UCA_SCHEDULE_NAME_PREFIX = 'UCA';

// EventBridge Scheduler substitutes this context attribute with the fire's scheduled time in the target Input.
export const SCHEDULER_SCHEDULED_TIME_PLACEHOLDER =
  '<aws.scheduler.scheduled-time>';

// Extra minutes the client adds on top of the backend minimum so a clamped
// start survives backend re-validation despite clock drift / request latency.
export const SCHEDULER_CLIENT_LEAD_BUFFER_MINUTES = 1;

/**
 * Reversible production kill-switch for Instagram publishing (Assistant tool
 * and flows step). Flip to false to pull publishing without a redeploy of
 * the gated code paths — the gateway's disabled-check, both branches of the
 * worker system prompt, and the web panel's unavailable-notice gate all stay
 * in place either way.
 * The flag compiles into both bundles, so a change always deploys api before
 * web — a web build showing the publish panel against an api that still
 * rejects publishing is the state to avoid.
 */
export const INSTAGRAM_PUBLISHING_ENABLED: boolean = true;
